import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Pencil, Eye, Trash2, Search } from "lucide-react";
import { toast } from "react-toastify";
import { getProperties, deleteProperty } from "../../../services/api";
import "./PropertyList.css";

function formatPrice(p) {
  if (p.priceLabel) return p.priceLabel;
  const raw = p.Price ?? p.price;
  if (raw === undefined || raw === null || raw === "") return "—";
  const n = Number(String(raw).replace(/[^\d.]/g, ""));
  if (!Number.isFinite(n) || n <= 0) return String(raw).trim() || "—";
  return `₹${n.toLocaleString("en-IN")}`;
}

function coverOf(p) {
  if (Array.isArray(p.images) && p.images.length) return p.images[0];
  return p.cover || p.image || "";
}

export default function PropertyList() {
  const [items, setItems] = useState([]);
  console.log("🚀 ~ file: PropertyList.js:31 ~ PropertyList ~ items", items);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");

  // Delete modal
  const [deleteId, setDeleteId] = useState(null);
  const [deleteName, setDeleteName] = useState("");
  const [deleting, setDeleting] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const list = await getProperties();
      setItems(Array.isArray(list) ? list : []);
    } catch (err) {
      toast.error(err.message || "Failed to load properties");
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const filtered = items.filter((p) => {
    if (cat) {
      const c = String(p.category || "").trim().toLowerCase();
      if (c !== cat.toLowerCase()) return false;
    }
    if (q.trim()) {
      const hay = `${p.title || p.name || ""} ${p.developer || p.builder || ""} ${
        p.locality || p.location || p.city || ""
      }`.toLowerCase();
      if (!hay.includes(q.toLowerCase())) return false;
    }
    return true;
  });

  function openDelete(p) {
    setDeleteId(p.id);
    setDeleteName(p.title || p.name || "this property");
  }

  function closeDelete() {
    if (deleting) return;
    setDeleteId(null);
    setDeleteName("");
  }

  async function confirmDelete() {
    if (!deleteId) return;
    setDeleting(true);
    try {
      await deleteProperty(deleteId);
      toast.success("Property deleted");
      setDeleteId(null);
      setDeleteName("");
      await refresh();
    } catch (err) {
      toast.error(err.message || "Delete failed");
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div className="pl">
      {/* Header */}
      <div className="pl-head">
        <div>
          <div className="pl-eyebrow">Inventory</div>
          <h1 className="pl-title">Properties</h1>
          <p className="pl-sub">
            Manage your entire portfolio — {items.length} listings.
          </p>
        </div>
        <Link to="/admin/add-property" className="pl-new">
          <Plus className="h-4 w-4" />
          New Property
        </Link>
      </div>

      {/* Filters */}
      <div className="pl-filters">
        <div className="pl-search">
          <Search className="pl-search-icon" />
          <input
            type="text"
            placeholder="Search by name, developer or location…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-input"
          />
        </div>
        <select
          value={cat}
          onChange={(e) => setCat(e.target.value)}
          className="pl-select"
        >
          <option value="">All categories</option>
          <option value="residential">Residential</option>
          <option value="commercial">Commercial</option>
          <option value="plots">Plots</option>
        </select>
      </div>

      {/* Table */}
      <div className="pl-wrap">
        <table className="pl-table">
          <thead>
            <tr>
              <th>Property</th>
              <th>Category</th>
              <th>City</th>
              <th>Price</th>
              <th>Status</th>
              <th>Tags</th>
              <th className="text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={7} className="pl-empty">
                  Loading properties…
                </td>
              </tr>
            )}

            {!loading &&
              filtered.map((p) => {
                const status = String(
                  p.status ?? (p.isActive || p.active !== false ? "Published" : "Draft")
                ).trim();
                const statusKey = status.toLowerCase().replace(/\s+/g, "-");
                const thumb = coverOf(p);

                return (
                  <tr key={p.id}>
                    <td>
                      <div className="pl-prop">
                        {thumb ? (
                          <img
                            src={thumb}
                            alt={p.title || p.name}
                            className="pl-thumb"
                          />
                        ) : (
                          <div className="pl-thumb-fallback" />
                        )}
                        <div>
                          <div className="pl-name">
                            {p.title ?? p.name ?? "—"}
                          </div>
                          <div className="pl-dev">
                            {p.developer ?? p.builder ?? "—"}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="capitalize">
                      {String(p.category || "—").trim()}
                    </td>
                    <td className="capitalize">
                      {String(p.city || "—").trim()}
                    </td>
                    <td>{formatPrice(p)}</td>
                    <td>
                      <span className={`pl-status pl-status--${statusKey}`}>
                        {status}
                      </span>
                    </td>
                    <td>
                      <div className="pl-tags">
                        {p.featured && (
                          <span className="pl-tag">Featured</span>
                        )}
                        {p.exclusive && (
                          <span className="pl-tag">Exclusive</span>
                        )}
                        {p.luxurious && (
                          <span className="pl-tag">Luxury</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="pl-actions">
                        <Link
                          to={`/property/${p.id}`}
                          className="icon-btn"
                          title="Preview"
                          target="_blank"
                          rel="noreferrer"
                        >
                          <Eye className="h-4 w-4" />
                        </Link>
                        <Link
                          to={`/admin/edit-property/${p.id}`}
                          className="icon-btn"
                          title="Edit"
                        >
                          <Pencil className="h-4 w-4" />
                        </Link>
                        <button
                          type="button"
                          onClick={() => openDelete(p)}
                          className="icon-btn icon-btn--danger"
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}

            {!loading && filtered.length === 0 && (
              <tr>
                <td colSpan={7} className="pl-empty">
                  No properties match your filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Delete confirmation modal */}
      {deleteId && (
        <div className="pl-modal-backdrop" role="dialog" aria-modal="true">
          <div className="pl-modal">
            <h3 className="pl-modal-title">Delete property?</h3>
            <p className="pl-modal-text">
              Are you sure you want to delete <strong>{deleteName}</strong>?
              This action cannot be undone.
            </p>
            <div className="pl-modal-actions">
              <button
                type="button"
                className="pl-btn-ghost"
                onClick={closeDelete}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="pl-btn-danger"
                onClick={confirmDelete}
                disabled={deleting}
              >
                {deleting ? "Deleting…" : "Yes, delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}